/**
 * Manage Subscription Dialog
 *
 * Staff/admin: move a customer onto a different plan (upgrade or downgrade,
 * no payment involved) or cancel their active subscription. Backed by the
 * admin-gated /api/subscriptions/customer/:customerId endpoints.
 */

import { useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Customer, SubscriptionPlan } from '@repo/types';
import { usePlans } from '@/hooks/usePlans';
import { useChangeCustomerPlan, useCancelCustomerSubscription } from '@/lib/hooks/useCustomers';

function formatPrice(plan: SubscriptionPlan): string {
  const amount = Math.round(Number(plan.price));
  const symbol = plan.currency === 'INR' ? '₹' : `${plan.currency} `;
  const duration =
    plan.duration_value === 1 ? plan.duration_unit : `${plan.duration_value} ${plan.duration_unit}s`;
  return `${symbol}${amount.toLocaleString('en-IN')} / ${duration}`;
}

/** Action verb for the confirm button, relative to the customer's current tier. */
function actionLabel(selected: SubscriptionPlan, currentTier: number | null): string {
  if (currentTier === null) return `Assign ${selected.name}`;
  if (selected.tier > currentTier) return `Upgrade to ${selected.name}`;
  if (selected.tier < currentTier) return `Downgrade to ${selected.name}`;
  return `Switch to ${selected.name}`;
}

export function ManageSubscriptionDialog({
  open,
  onOpenChange,
  customer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer;
}) {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const { data: plansData, isLoading } = usePlans(1, 50);
  const changePlan = useChangeCustomerPlan(customer.id);
  const cancelSubscription = useCancelCustomerSubscription(customer.id);

  const currentPlanId = customer.subscription?.plan_id ?? null;
  const currentTier = customer.subscription?.tier ?? null;
  const plans = (plansData?.data ?? [])
    .filter((plan) => plan.active && !plan.is_internal)
    .sort((a, b) => a.tier - b.tier);
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) ?? null;
  const busy = changePlan.isPending || cancelSubscription.isPending;

  const close = () => {
    setSelectedPlanId(null);
    onOpenChange(false);
  };

  const handleChangePlan = () => {
    if (!selectedPlan) return;
    changePlan.mutate(selectedPlan.id, { onSuccess: close });
  };

  const handleCancelSubscription = () => {
    setConfirmCancelOpen(false);
    cancelSubscription.mutate(undefined, { onSuccess: close });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => (busy ? undefined : next ? onOpenChange(true) : close())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Subscription</DialogTitle>
            <DialogDescription>
              Change {customer.name}&apos;s plan. This takes effect immediately and does not charge the
              customer.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <ScrollArea className="max-h-80 pr-3">
              <div className="space-y-2" role="radiogroup" aria-label="Subscription plans">
                {plans.map((plan) => {
                  const isCurrent = plan.id === currentPlanId;
                  const isSelected = plan.id === selectedPlanId;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={isCurrent || busy}
                      onClick={() => setSelectedPlanId(plan.id)}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors',
                        isSelected ? 'border-primary bg-primary/5' : 'hover:bg-accent',
                        isCurrent && 'cursor-default opacity-70'
                      )}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{plan.name}</span>
                          <Badge variant="outline">Tier {plan.tier}</Badge>
                          {isCurrent && <Badge variant="secondary">Current</Badge>}
                        </div>
                        <p className="mt-0.5 text-sm text-muted-foreground">{formatPrice(plan)}</p>
                      </div>
                      {isSelected && <CheckCircle2 className="size-5 shrink-0 text-primary" aria-hidden />}
                    </button>
                  );
                })}
                {plans.length === 0 && (
                  <p className="py-6 text-center text-sm text-muted-foreground">No plans available.</p>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            {customer.subscription ? (
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={busy}
                onClick={() => setConfirmCancelOpen(true)}
              >
                {cancelSubscription.isPending && <Loader2 className="size-4 animate-spin" />}
                Cancel subscription
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" disabled={busy} onClick={close}>
                Close
              </Button>
              <Button disabled={!selectedPlan || busy} onClick={handleChangePlan}>
                {changePlan.isPending && <Loader2 className="size-4 animate-spin" />}
                {selectedPlan ? actionLabel(selectedPlan, currentTier) : 'Change plan'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              {customer.name} will immediately lose access to their{' '}
              {customer.subscription?.plan_name || `Tier ${customer.subscription?.tier}`} plan benefits.
              This cannot be undone — they would need to be re-assigned a plan or purchase one again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleCancelSubscription}
            >
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
